import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Briefcase, Home, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const T = {
  heading: { de: 'Unsere Unternehmen', en: 'Our Companies' },
  sub: { de: 'Drei spezialisierte Tochterunternehmen – vereint unter dem Dach der Basic United Holding', en: 'Three specialised subsidiaries – united under the Basic United Holding umbrella' },
  learnMore: { de: 'Mehr erfahren', en: 'Learn more' },
};

const subsidiaries = [
  {
    id: 'atob-logistic', name: 'AtoB Logistic Solutions GmbH',
    category: { de: 'Logistik & Transport', en: 'Logistics & Transport' },
    description: { de: 'Spezialisiert auf Fernverkehr mit 40t-LKW und Nahverkehr mit 3,5t-Fahrzeugen inkl. Ladebordwand.', en: 'Specialised in long-distance transport with 40t trucks and local delivery with 3.5t vehicles incl. tail lift.' },
    icon: Truck, logo: '/images/atob-logistic-logo-transparent.png', image: '/images/atob-truck-highway.jpg',
    services: { de: ['Fernverkehr (40t)', 'Nahverkehr (3,5t)', 'Komplettlösungen'], en: ['Long-haul (40t)', 'Local delivery (3.5t)', 'Complete solutions'] }
  },
  {
    id: 'dsb-business', name: 'DSB Business Solutions GmbH',
    category: { de: 'Unternehmensberatung', en: 'Business Consulting' },
    description: { de: 'Expertenwissen in Datenschutz, IT-Sicherheit, HR-Beratung und digitaler Transformation.', en: 'Expert knowledge in data protection, IT security, HR consulting and digital transformation.' },
    icon: Briefcase, logo: '/images/edited-dsb-logo.png', image: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=800',
    services: { de: ['Datenschutz & DSGVO', 'IT-Sicherheit', 'HR-Beratung', 'Digitale Transformation'], en: ['Data Protection & GDPR', 'IT Security', 'HR Consulting', 'Digital Transformation'] }
  },
  {
    id: 'atob-entruempelung', name: 'AtoB Objektservice GmbH',
    category: { de: 'Entrümpelung & Entsorgung', en: 'Clearance & Disposal' },
    description: { de: 'Professionelle Haushaltsauflösungen, Entrümpelungen und Entsorgungsdienstleistungen.', en: 'Professional household clearances, decluttering and disposal services.' },
    icon: Home, logo: '/images/atob-objektservice-logo-dark-bg.png', image: 'https://images.unsplash.com/photo-1762176211744-735731ee117b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    services: { de: ['Haushaltsauflösungen', 'Entrümpelung', 'Entsorgung & Recycling', 'Umzugsservice'], en: ['Household Clearances', 'Decluttering', 'Disposal & Recycling', 'Moving Service'] }
  }
];

export const SubsidiariesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { language } = useLanguage();

  return (
    <section ref={ref} data-testid="subsidiaries-section" className="section-spacing bg-[#0F1A2E]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-16">
          <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">{T.heading[language]}</h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">{T.sub[language]}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {subsidiaries.map((subsidiary, index) => (
            <motion.div key={subsidiary.id} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 * (index + 1) }} data-testid={`subsidiary-card-${subsidiary.id}`} className="group bg-[#152238] rounded-2xl overflow-hidden border border-[#1e3a5f] hover:border-[#F5A623]/30 transition-all duration-500">
              <div className="relative h-48 overflow-hidden">
                <img src={subsidiary.image} alt={subsidiary.name} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#152238] via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="inline-block bg-[#F5A623] text-[#0F1A2E] text-xs uppercase tracking-wider px-3 py-1 font-semibold rounded-full">{subsidiary.category[language]}</span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="mb-6">
                  <img src={subsidiary.logo} alt={subsidiary.name} className="h-20 md:h-24 w-auto object-contain" />
                </div>
                <p className="text-white/60 mb-6 leading-relaxed">{subsidiary.description[language]}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {subsidiary.services[language].map((service) => (
                    <span key={service} className="text-xs text-white/50 bg-white/5 px-3 py-1 rounded-full border border-white/10">{service}</span>
                  ))}
                </div>
                <Link to="/unternehmen" className="inline-flex items-center text-[#F5A623] font-semibold text-sm uppercase tracking-wider group-hover:text-white transition-colors">
                  {T.learnMore[language]}
                  <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SubsidiariesSection;
